import { Divider, Tab, Tabs } from '@material-ui/core';
import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useStyles } from './styles';

export interface ApprovalsTab {
  /** Stable identifier. Appears in the URL, so never renumber or reorder it. */
  id: string;
  label: MessageDescriptor;
}

interface ApprovalsTabsProps {
  tabs: ApprovalsTab[];
  value: string;
  onChange: (id: string) => void;
  /** Names the strip for screen readers — usually the page title. */
  ariaLabel: string;
}

export const tabPanelId = (id: string) => `approvals-tabpanel-${id}`;
const tabId = (id: string) => `approvals-tab-${id}`;

/**
 * The page-level tab strip for the approvals surface (AXHE-4472).
 *
 * Not `components/TabNavigation`, which is the applicant-profile bar: it is
 * `centered` + `variant="fullWidth"` with uppercase `caption` labels and a
 * hardcoded `aria-label`, and it keys tabs by numeric index. The design here is
 * a left-aligned sentence-case strip, and the index is the specific thing to
 * avoid — see the note on `id` below. `TabPanel` in FDS is `@deprecated` and
 * also index-based, so panels are plain divs using `tabPanelId`.
 *
 * The design has three tabs — Approver rules, Approvals, Auto-approval — but
 * only the first has anything behind it today (the queue is AXHE-3934,
 * auto-approval is AXHE-3975, both still to be built). So `tabs` is a registry
 * the page owns, and this renders **nothing at all** below two entries: a strip
 * whose other tabs lead nowhere is a dead end, and a strip of one is a label
 * pretending to be a control. Landing either sibling means adding one entry
 * here, at which point the strip appears on its own.
 */
export const ApprovalsTabs: React.FC<ApprovalsTabsProps> = ({
  tabs,
  value,
  onChange,
  ariaLabel,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  if (tabs.length < 2) return null;

  return (
    <>
      <Tabs
        aria-label={ariaLabel}
        className={styles.pageTabs}
        indicatorColor="primary"
        onChange={(_event, next: string) => onChange(next)}
        textColor="primary"
        value={value}
        variant="standard"
      >
        {tabs.map(tab => (
          <Tab
            aria-controls={tabPanelId(tab.id)}
            id={tabId(tab.id)}
            key={tab.id}
            label={intl.formatMessage(tab.label)}
            value={tab.id}
          />
        ))}
      </Tabs>
      <Divider className={styles.pageTabsDivider} />
    </>
  );
};
